// share-url-fix 2026-06-05
import React, { useState, useEffect } from 'react';
import { X, Film, Image as ImageIcon, Loader2, ExternalLink, Copy, Check } from 'lucide-react';
import { auth } from '../firebase';
import AdLeadsModal from './AdLeadsModal';

interface AdMediaViewerProps {
  propertyId: string;
  onClose: () => void;
  propertyAddress?: {
    street: string;
    houseNumber: string;
    city: string;
  };
  onLeadClick?: (leadId: string) => void;
}

interface PropertyMedia {
  id: string;
  media_url: string;
  media_type: string;
  property_id: string;
  created_at: string;
  time_step?: string;
  ad_type?: string;
  ad_variant?: string;
  leads_count?: number;
  user_id?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  agentEmail?: string | null;
}

export default function AdMediaViewer({ propertyId, onClose, propertyAddress, onLeadClick }: AdMediaViewerProps) {
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState<PropertyMedia[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [selectedAdLeads, setSelectedAdLeads] = useState<any[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  useEffect(() => {
    loadPropertyMedia();
  }, [propertyId]);

  const loadPropertyMedia = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;

      if (!user) {
        setError('משתמש לא מחובר');
        setLoading(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const idToken = await user.getIdToken();

      const response = await fetch(`${supabaseUrl}/functions/v1/get-property-media`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'X-Firebase-Auth': 'true',
          'Content-Type': 'application/json',
        },
      });

      const rawText = await response.text();
      console.log('[AdMediaViewer] status', response.status, 'body', rawText.slice(0, 500));

      let data: any;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        throw new Error('שגיאת ניתוח תגובה: ' + rawText.slice(0, 200));
      }

      if (!response.ok) {
        const msg = (typeof data === 'object' && data !== null && data.error) ? data.error : 'שגיאה בטעינת מדיה';
        throw new Error(msg);
      }

      if (!Array.isArray(data)) {
        console.error('[AdMediaViewer] unexpected response shape', data);
        throw new Error('תגובה לא צפויה מהשרת');
      }

      console.log('[AdMediaViewer] total items', data.length, 'filtering for propertyId', propertyId);
      // Filter only media for this specific property
      const filteredMedia = data.filter((item: PropertyMedia) => item.property_id === propertyId);
      console.log('[AdMediaViewer] filtered items', filteredMedia.length, filteredMedia[0]);
      setMedia(filteredMedia);
    } catch (err: any) {
      console.error('[AdMediaViewer] loadPropertyMedia error', err);
      setError(err.message || 'שגיאה בטעינת מדיה');
    } finally {
      setLoading(false);
    }
  };

  const isShareAd = (url: string | null | undefined) => {
    return !!url && url.includes('/ads/share/');
  };

  const extractAgentIdFromUrl = (url: string): string => {
    const pathMatch = url.match(/\/ads\/share\/([^\/]+)\//);
    return pathMatch ? pathMatch[1] : '';
  };

  const buildShareUrl = (item: PropertyMedia): string => {
    const agentId = extractAgentIdFromUrl(item.media_url || '');
    const mode = item.ad_type === 'seller' ? 'seller' : 'buyer';
    // Strip any trailing 13-digit Unix timestamp that Make may have appended to property_id
    const cleanRid = (item.property_id || '').replace(/_\d{13}$/, '');
    const finalUrl = `https://lp.realtyai-value.com/s/${agentId}?rid=${encodeURIComponent(cleanRid)}&mode=${mode}&v=${encodeURIComponent(String(item.created_at))}`;
    console.log('SHARE_URL_DEBUG', {
      rawPropertyId: item.property_id,
      cleanRid,
      mediaUrl: item.media_url,
      createdAt: item.created_at,
      finalUrl,
    });
    return finalUrl;
  };

  const copyToClipboard = async (item: PropertyMedia, isForFacebook: boolean = false) => {
    try {
      const urlToCopy = isForFacebook ? buildShareUrl(item) : item.media_url;
      await navigator.clipboard.writeText(urlToCopy);
      setCopiedId(item.id);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  const handleLeadsClick = async (propertyId: string, agentId: string) => {
    try {
      setLoadingLeads(true);
      setShowLeadsModal(true);
      setSelectedAdLeads([]);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('משתמש לא מחובר');
      }

      // Strip trailing 13-digit Unix timestamp that Make may append
      const cleanPropertyId = (propertyId || '').replace(/_\d{13}$/, '');
      console.log('[AdLeads] query', { rawPropertyId: propertyId, cleanPropertyId, agentId });

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const idToken = await user.getIdToken();

      const response = await fetch(`${supabaseUrl}/functions/v1/get-leads-by-ad`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          property_id: cleanPropertyId,
          agent_id: agentId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'שגיאה בטעינת לידים');
      }

      const data = await response.json();
      setSelectedAdLeads(data.leads || []);
    } catch (err: any) {
      console.error('Error loading leads:', err);
      alert('שגיאה בטעינת לידים: ' + err.message);
    } finally {
      setLoadingLeads(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-[70] flex items-center justify-center p-4" dir="rtl">
      <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[95vh] flex flex-col">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Film className="text-blue-400" size={24} />
            {propertyAddress
              ? `מודעות שנוצרו לנכס בכתובת ${propertyAddress.street} ${propertyAddress.houseNumber}, ${propertyAddress.city}`
              : 'מודעות שנוצרו לנכס'
            }
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-blue-400 mb-4" />
              <p className="text-gray-400">טוען מודעות...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-400">שגיאה: {error}</p>
            </div>
          ) : media.length === 0 ? (
            <div className="text-center py-12">
              <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">עדיין לא נוצרו מודעות לנכס זה</p>
              <p className="text-gray-500 text-sm mt-2">המודעות יופיעו כאן לאחר יצירתן ב-MAKE</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {media.map((item) => (
                <div key={item.id} className="bg-gray-700 rounded-lg overflow-hidden flex flex-col">
                  <div className="relative bg-gray-900 flex items-center justify-center" style={{ minHeight: '400px' }}>
                    {(item.media_type || '').toLowerCase().includes('video') || (item.media_type || '').toLowerCase() === 'mp4' ? (
                      <video
                        controls
                        className="w-full h-full object-contain max-h-[500px]"
                        src={item.media_url}
                      >
                        הדפדפן שלך לא תומך בהצגת וידאו
                      </video>
                    ) : (
                      <img
                        src={item.media_url}
                        alt="מודעה"
                        className="w-full h-full object-contain max-h-[500px]"
                      />
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {(item.media_type || '').toLowerCase().includes('video') || (item.media_type || '').toLowerCase() === 'mp4' ? (
                          <Film className="w-4 h-4 text-blue-400" />
                        ) : (
                          <ImageIcon className="w-4 h-4 text-green-400" />
                        )}
                        <span className="text-sm text-gray-300">
                          {item.media_type.toLowerCase().includes('video') || item.media_type.toLowerCase() === 'mp4' ? 'וידאו' : 'תמונה'}
                        </span>
                      </div>

                      <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-300">
                        מוכן
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {isShareAd(item.media_url) && (
                        <button
                          onClick={() => handleLeadsClick(item.property_id, item.user_id || item.agentId || '')}
                          className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-green-500/30 transition-colors cursor-pointer"
                        >
                          לידים: {item.leads_count || 0}
                        </button>
                      )}
                      {item.ad_type && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          {item.ad_type === 'buyer' ? '👤 קונה' : '💰 מוכר'}
                        </span>
                      )}
                      {item.ad_variant && (
                        <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                          {item.ad_variant === 'info' && '📋 מידע'}
                          {item.ad_variant === 'opportunity' && '🎯 הזדמנות'}
                          {item.ad_variant === 'emotion' && '❤️ רגש'}
                          {item.ad_variant === 'proof' && '✅ הוכחת יכולת'}
                          {item.ad_variant === 'valuation' && '💎 הערכת שווי'}
                          {item.ad_variant === 'pain' && '🔥 נקודת כאב'}
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-gray-500 mb-2">
                      נוצר: {new Date(item.time_step || item.created_at).toLocaleDateString('he-IL')} {' '}
                      {new Date(item.time_step || item.created_at).toLocaleTimeString('he-IL', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>

                    {(item.agentName || item.agentEmail || item.agentId) && (
                      <div className="text-xs px-2 py-0.5 rounded-full inline-block mb-2 bg-teal-900/60 text-teal-300 border border-teal-700/40">
                        סוכן: {item.agentName || item.agentEmail || item.agentId}
                      </div>
                    )}

                    <div className="text-xs text-gray-400 mb-3 truncate" title={item.property_id}>
                      מזהה: {item.property_id}
                    </div>

                    <div className="flex flex-col gap-2">
                      {isShareAd(item.media_url) ? (
                        <button
                          onClick={() => copyToClipboard(item, true)}
                          className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors text-sm"
                        >
                          {copiedId === item.id ? (
                            <>
                              <Check className="w-4 h-4" />
                              הקישור הועתק!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              העתק קישור לפייסבוק
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => copyToClipboard(item, false)}
                          className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors text-sm"
                        >
                          {copiedId === item.id ? (
                            <>
                              <Check className="w-4 h-4" />
                              הקישור הועתק!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              העתק קישור
                            </>
                          )}
                        </button>
                      )}

                      <a
                        href={item.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors text-sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                        פתח בחלון חדש
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AdLeadsModal
        isOpen={showLeadsModal}
        onClose={() => setShowLeadsModal(false)}
        leads={selectedAdLeads}
        isLoading={loadingLeads}
        onLeadClick={(leadId) => {
          setShowLeadsModal(false);
          onLeadClick?.(leadId);
        }}
      />
    </div>
  );
}
